import {Observable, throwError as observableThrowError} from 'rxjs';
import {environment} from '../../../environments/environment';
import {HttpClient, HttpHeaders, HttpParams, HttpResponse} from '@angular/common/http';
import { Injectable, inject } from '@angular/core';

import {HydraCollection} from '../../class/generic/hydra-collection';
import {LocalStorageService} from 'ngx-webstorage';
import {api} from '../../api/api-dictionnary';
import {Utilisateur} from '../../interface/api/Utilisateur';
import {catchError, map, takeUntil} from 'rxjs/operators';
import {SubscribeService} from './subscribe.service';
import {ActivatedRoute, Router} from '@angular/router';
import {Country} from '../../interface/api/Country';

@Injectable()
export class HydraService {
    private httpClient = inject(HttpClient);
    private storage = inject(LocalStorageService);
    private _subscribeService = inject(SubscribeService);
    private router = inject(Router);
    private activatedRoute = inject(ActivatedRoute);


    /**
     * @returns
     * @param item1
     * @param item2
     */
    static assertItemsAreSameResource(item1: any, item2: any): boolean {
        if (item1 === item2) {
            return true;
        }
        if (item1 === null || item2 === null) {
            return false;
        }

        const IRI1 = HydraService.extractIRI(item1);
        const IRI2 = HydraService.extractIRI(item2);

        /*
         Attention : Au moins une des IRI n'a pu etre résolue, mais null n'est pas sa vraie valeur !
         Si les 2 IRI n'ont pu être résolues, la condition IRI1 === IRI2 renverrait true, ce qui serait probablement
         une erreur. Dans un cadre plus strict on pourrait lever une exception.
         */
        if (IRI1 === null || IRI2 === null) {
            return false;
        }

        return IRI1 === IRI2;
    }

    /**
     *
     * If stringOrObject is a string, returns it if it is a valid IRI
     * If stringOrObject is an object, returns the @id key
     * otherwise returns null
     *
     * @param stringOrObject
     * @returns
     */
    static extractIRI(stringOrObject): string | null {
        if (stringOrObject === null) {
            return null;
        }

        if (typeof stringOrObject === 'string') {
            if (stringOrObject.indexOf('/api') !== -1) {
                return stringOrObject;
            } else {
                return null;
            }
        }

        if (typeof stringOrObject !== 'object') {
            return null;
        }

        if (!stringOrObject.hasOwnProperty('@id')) {
            return null;
        }

        return stringOrObject['@id'];
    }

    static appendOrderToURL(selectedSort: any, url: URL): URL {
        if (selectedSort && selectedSort.sorts.length) {
            url.searchParams.append('order[' + selectedSort.sorts[0].prop + ']', selectedSort.sorts[0].dir);
        }
        return url;
    }

    static appendMaterialOrderToURL(sort: { active: string; direction: string }, url: URL): URL {
        if (sort) {
            url.searchParams.append('order[' + sort.active + ']', sort.direction);
        }
        return url;
    }

    public static getFileNameFromResponseContentDisposition(res: HttpResponse<Blob>): string {
        const contentDisposition = res.headers.get('content-disposition') || '';
        const matches = /filename=([^;]+)/ig.exec(contentDisposition);
        return (matches[1] || 'untitled').trim();
    };

    public getList(url: string, cache = false) {
        if (url.indexOf(environment.apiHost) !== 0) {
            url = environment.apiHost + url;
        }

        return this.httpClient.get(url).pipe(
            takeUntil(this._subscribeService.addRef(url, 'get')),
            map(res => {
                if (url === environment.apiHost + api.countries) {
                    const pays = new HydraCollection(res).getMember();
                    const FrCountry: Country = pays.find(item => item.code === 'FR');
                    if (FrCountry) {
                        const listWithoutFR = pays.filter(item => item.code !== 'FR') as Country[];
                        res['hydra:member'] = [FrCountry, ...listWithoutFR];
                    }
                }

                return new HydraCollection(res);
            }),
            catchError(caught => observableThrowError(caught))
        );
    }

    public getItem(iri: string) {
        return this.httpClient.get(environment.apiHost + iri).pipe(
            takeUntil(this._subscribeService.addRef(iri, 'get')),
            map(res => res)
        );
    }

    /**
     * @deprecated nom de fonction incorrect, use getItem() instead
     * @param url
     */
    public getById(url: string) {
        return this.getItem(url);
    }

    public getHtml(url: string) {
        return this.httpClient
            .get(environment.apiHost + url, {responseType: 'text'})
            .pipe(
                takeUntil(this._subscribeService.addRef(url, 'get')),
                map(res => res)
            );
    }

    public getBinary(url: string, options?: {
                         headers?: HttpHeaders | {
                             [header: string]: string | string[];
                         };
                         params?: HttpParams | {
                             [param: string]: string | string[];
                         };
                         reportProgress?: boolean;
                         withCredentials?: boolean;
                     }
    ): Observable<HttpResponse<Blob>> {
        if (!url.includes(environment.apiHost)) {
            url = '' + environment.apiHost + url;
        }

        return this.httpClient.get(url, {
                ...options,
                observe: 'response',
                responseType: 'blob'
            }
        );
    }

    public postBinary(url: string, body: object, options?: {
        headers?: HttpHeaders | {
            [header: string]: string | string[];
        };
    }): Observable<HttpResponse<Blob>> {
        return this.httpClient.post(this.appendHost(url), body, {
            ...options,
            observe: 'response',
            responseType: 'blob',
        });
    }

    public appendHost(url: string): string {
        const hostString = environment.apiHost;
        if (url.indexOf(environment.apiHost) !== 0) {
            return environment.apiHost + url;
        }
        return url;
    }

    public put(url, item) {
        url = this.appendHost(url);
        return this.httpClient.put(url, item).pipe(
            takeUntil(this._subscribeService.addRef(url, 'put')),
            map(res => res)
        );
    }

    public save(url, item) {
        url = this.appendHost(url);

        if ((item['@id'] ?? false) || (item['id'] ?? false)) {

            // si l'URL est celle du POST et qu'on a un objet avec une IRI, on l'utilise
            if (item.hasOwnProperty('@id') && item['@id'] != null) {
                const realURL = new URL(url);
                const urlWithoutId = item['@id'].split('/').slice(0, -1).join('/');
                if (realURL.pathname === urlWithoutId) {
                    url = item['@id'];
                }
            }

            return this.put(url, item);
        } else {
            return this.post(url, item);
        }
    }

    public post(url, item) {
        url = this.appendHost(url);
        return this.httpClient.post(url, item).pipe(
            takeUntil(this._subscribeService.addRef(url, 'post')),
            map(res => res)
        );
    }

    public upload(file: File,
                  code: string = 'public',
                  forceOwner: Utilisateur = null) {
        const formData: FormData = new FormData();
        formData.append('fichier', file, file.name);
        formData.append('fichierType', code);

        if (forceOwner != null) {
            formData.append('owner', forceOwner['@id']);
        }

        const url = environment.apiHost + api.fichier;
        return this.httpClient
            .post(url, formData)
            .pipe(
                takeUntil(this._subscribeService.addRef(api.fichier, 'post')),
                map(res => res)
            );
    }

    public delete(url) {
        return this.httpClient.delete(environment.apiHost + url).pipe(
            takeUntil(this._subscribeService.addRef(url, 'delete')),
            map(res => res)
        );
    }

    public beforeSaveHydraRelation(item, index: string[]) {
        const prepare = item;
        index.forEach(key => {
            const toParse = prepare[key];
            if (
                toParse !== undefined &&
                toParse != null &&
                Array.isArray(toParse)
            ) {
                prepare[key] = [];
                for (let i = 0; i < toParse.length; i++) {
                    if (toParse[i]['@id'] !== undefined)
                        {prepare[key].push(toParse[i]['@id']);}
                }
            } else {
                if (
                    toParse != null &&
                    typeof toParse === 'object' &&
                    toParse['@id'] !== undefined
                )
                    {prepare[key] = toParse['@id'];}
            }
        });
        return prepare;
    }


    /**
     * Traverses a router tree from root to a leaf looking for {@param}.
     */
    getParam(param: string): string | null {
        for (const route of this.getCurrentRoutesChain()) {
            if (route.snapshot.paramMap.has(param)) {
                return route.snapshot.paramMap.get(param);
            }
        }
        return null;
    }

    /**
     * Retrieves a chain of {ActivatedRoutes} from root to a leaf.
     */
    getCurrentRoutesChain(): ActivatedRoute[] {
        let currentRoute = this.router.routerState.root;
        const results: ActivatedRoute[] = [currentRoute];

        while (currentRoute.firstChild) {
            currentRoute = currentRoute.firstChild;
            results.push(currentRoute);
        }

        return results;
    }

    public extractIRI(stringOrObject): string | null {
        return HydraService.extractIRI(stringOrObject);
    }
}
